import type { OrgLocation } from '@/auth/auth-api';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { ReferralRow } from '@/types/referral-api';
import { roleLabel } from './referral-config';

/** The create-appointment form's comment limit (appointment-form.ts: z.string().max(500)). */
export const COMMENT_MAX = 500;

/** "Referral to Dietitian: {reason}. Requested by {referrerName}." Parts the row lacks are left out. */
export function buildReferralComment(row: ReferralRow): string {
  const reason = row.reasonDisplay?.trim();
  const referrer = row.referrerName?.trim();
  const role = row.performerRole?.trim() ? roleLabel(row.performerRole) : '';
  const head = `Referral${role ? ` to ${role}` : ''}${reason ? `: ${reason}` : ''}.`;
  const text = referrer ? `${head} Requested by ${referrer}.` : head;
  return text.length > COMMENT_MAX ? `${text.slice(0, COMMENT_MAX - 1).trimEnd()}…` : text;
}

/**
 * Prefill for the create-appointment panel (same approach as
 * src/patients/lib/build-create-appointment-prefill.ts): the patient only, NO provider (there is no
 * specialty filter yet), plus the referral comment. The row carries the patient's id, name and MRN;
 * the panel loads the rest once a provider and profile are picked.
 * `location` is the one the Calendar is showing; it must already be guarded against an empty id (pass null), like the other callers.
 */
export function buildReferralBookingPrefill(
  row: ReferralRow,
  location: OrgLocation | null,
  selectedDateISO: string,
): Partial<AppointmentFormValues> {
  const id = location?.id;
  const locationHasRealId = typeof id === 'string' && id.trim().length > 0 && id.trim() !== '0';
  return {
    location: locationHasRealId ? { id: location!.id ?? '', name: location!.name ?? '' } : null,
    patientId: row.patient.id,
    patientName: row.patient.name?.trim() || null,
    selectedDate: selectedDateISO,
    comment: buildReferralComment(row),
  };
}
